import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { formatShortDate, weekdayLabel } from '../lib/date';
import { isValidPurchaseQuantity, requiresIntegerQuantity } from '../lib/purchase-quantity';
import type {
  FixedPurchaseOccurrence,
  FixedPurchaseOverride,
  FixedPurchaseRule,
  FixedPurchaseSourceType,
  ResolvedFixedPurchases,
} from '../types';
import { CheckIcon, ChevronRightIcon, EditIcon, RefreshIcon } from './Icons';
import { Modal } from './Modal';

interface Props {
  open: boolean;
  weekStart: string;
  weekEnd: string;
  rules: FixedPurchaseRule[];
  resolved?: ResolvedFixedPurchases;
  overrides: FixedPurchaseOverride[];
  onClose: () => void;
  onApply: (overrides: FixedPurchaseOverride[]) => Promise<void>;
}

interface AdjustmentLine {
  ruleId: string;
  date?: string;
  name: string;
  sourceType: FixedPurchaseSourceType;
  unit?: string;
  quantity?: string;
  batchCount?: number;
  adjusted: boolean;
  disabled: boolean;
  restoringDefault?: boolean;
}

function overrideKey(value: Pick<FixedPurchaseOverride, 'ruleId' | 'date'>): string {
  return `${value.ruleId}\u0000${value.date ?? ''}`;
}

function lineFromOccurrence(occurrence: FixedPurchaseOccurrence): AdjustmentLine {
  return {
    ruleId: occurrence.ruleId,
    date: occurrence.date,
    name: occurrence.name,
    sourceType: occurrence.sourceType,
    unit: occurrence.unit,
    quantity: occurrence.quantity,
    batchCount: occurrence.batchCount,
    adjusted: occurrence.adjusted,
    disabled: false,
  };
}

function disabledLine(
  skipped: ResolvedFixedPurchases['skipped'][number],
  rule?: FixedPurchaseRule,
): AdjustmentLine {
  return {
    ruleId: skipped.ruleId,
    date: skipped.date,
    name: skipped.name || rule?.name || '固定采购',
    sourceType: rule?.sourceType ?? 'ingredient',
    unit: rule?.unit,
    quantity: rule?.quantity,
    batchCount: rule?.batchCount,
    adjusted: true,
    disabled: true,
  };
}

function lineAmount(line: AdjustmentLine): string {
  if (line.restoringDefault) return '恢复默认（待保存）';
  if (line.disabled) return '本周不采购';
  return line.sourceType === 'dish' && line.quantity === undefined
    ? `${line.batchCount ?? 1}批配方`
    : `${line.quantity ?? '—'}${line.unit ?? ''}`;
}

export function WeeklyFixedPurchaseAdjustments({
  open,
  weekStart,
  weekEnd,
  rules,
  resolved,
  overrides,
  onClose,
  onApply,
}: Props) {
  const [draftOverrides, setDraftOverrides] = useState<FixedPurchaseOverride[]>(overrides);
  const [editing, setEditing] = useState<AdjustmentLine | null>(null);
  const [draftAmount, setDraftAmount] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setDraftOverrides(overrides.map((item) => ({ ...item })));
    setEditing(null);
    setError('');
  }, [open, overrides]);

  const baseLines = useMemo(() => {
    const active = [
      ...(resolved?.daily ?? []).map(lineFromOccurrence),
      ...(resolved?.weekly ?? []).map(lineFromOccurrence),
    ];
    const activeKeys = new Set(active.map(overrideKey));
    const disabled = (resolved?.skipped ?? [])
      .filter((item) => item.reason === 'week_override' && !activeKeys.has(overrideKey(item)))
      .map((item) => disabledLine(item, rules.find((rule) => rule.id === item.ruleId)));
    return [...active, ...disabled];
  }, [resolved, rules]);

  const lines = useMemo(() => {
    const originalByKey = new Map(overrides.map((item) => [overrideKey(item), item]));
    const draftByKey = new Map(draftOverrides.map((item) => [overrideKey(item), item]));
    return baseLines.map((line): AdjustmentLine => {
      const key = overrideKey(line);
      const original = originalByKey.get(key);
      const draft = draftByKey.get(key);

      // Removing a previously-saved override means "restore the rule default".
      // The current response does not expose that historical default amount,
      // so show an explicit pending state instead of guessing from a newer rule
      // revision. The server will return the exact resolved value after save.
      if (original && !draft) {
        return {
          ...line,
          disabled: false,
          adjusted: true,
          restoringDefault: true,
        };
      }
      if (!draft) return line;
      if (draft.enabled === false) {
        return {
          ...line,
          disabled: true,
          adjusted: true,
          restoringDefault: false,
        };
      }
      return {
        ...line,
        disabled: false,
        adjusted: true,
        restoringDefault: false,
        ...(draft.quantity !== undefined ? { quantity: draft.quantity } : {}),
        ...(draft.batchCount !== undefined ? { batchCount: draft.batchCount } : {}),
      };
    });
  }, [baseLines, draftOverrides, overrides]);

  const dailyGroups = useMemo(() => {
    const groups = new Map<string, AdjustmentLine[]>();
    for (const line of lines.filter((item) => item.date)) {
      const rows = groups.get(line.date!) ?? [];
      rows.push(line);
      groups.set(line.date!, rows);
    }
    return [...groups.entries()].sort(([left], [right]) => left.localeCompare(right));
  }, [lines]);
  const weeklyLines = lines.filter((line) => !line.date);
  const changed = JSON.stringify(draftOverrides) !== JSON.stringify(overrides);

  const openLine = (line: AdjustmentLine) => {
    setEditing(line);
    setDraftAmount(line.sourceType === 'dish' && line.quantity === undefined
      ? String(line.batchCount ?? 1)
      : String(line.quantity ?? ''));
    setError('');
  };

  const replaceOverride = (line: AdjustmentLine, value?: FixedPurchaseOverride) => {
    const key = overrideKey(line);
    setDraftOverrides((current) => [
      ...current.filter((item) => overrideKey(item) !== key),
      ...(value ? [value] : []),
    ]);
  };

  const saveLine = (event: FormEvent) => {
    event.preventDefault();
    if (!editing) return;
    if (editing.sourceType === 'dish' && editing.quantity === undefined) {
      const batchCount = Number(draftAmount);
      if (!Number.isInteger(batchCount) || batchCount < 1 || batchCount > 99) {
        setError('请输入1到99的整数批次');
        return;
      }
      replaceOverride(editing, {
        ruleId: editing.ruleId,
        date: editing.date,
        enabled: true,
        batchCount,
      });
    } else {
      if (!isValidPurchaseQuantity(draftAmount, editing.unit ?? '')) {
        setError(requiresIntegerQuantity(editing.unit ?? '') ? '该单位需要填写大于0的整数' : '请输入大于0的采购数量');
        return;
      }
      replaceOverride(editing, {
        ruleId: editing.ruleId,
        date: editing.date,
        enabled: true,
        quantity: draftAmount.trim(),
      });
    }
    setEditing(null);
  };

  const disableLine = () => {
    if (!editing) return;
    replaceOverride(editing, {
      ruleId: editing.ruleId,
      date: editing.date,
      enabled: false,
    });
    setEditing(null);
  };

  const restoreLine = () => {
    if (!editing) return;
    replaceOverride(editing);
    setEditing(null);
  };

  const submit = async () => {
    setSaving(true);
    setError('');
    try {
      await onApply(draftOverrides);
      onClose();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '本周固定采购保存失败，请重试');
    } finally {
      setSaving(false);
    }
  };

  const close = () => {
    if (editing) {
      setEditing(null);
      setError('');
      return;
    }
    onClose();
  };

  return (
    <Modal
      open={open}
      title={editing ? `调整${editing.name}` : '本周固定采购'}
      eyebrow={editing ? '只修改这一周' : `${formatShortDate(weekStart)}—${formatShortDate(weekEnd)}`}
      size="large"
      variant="mobile-page"
      bodyScrollable
      onClose={close}
    >
      {editing ? (
        <form className="week-fixed-editor" data-qa="weekly-fixed-purchase-editor" onSubmit={saveLine}>
          <div className="inline-alert"><EditIcon size={20} /><span>这里只改本周，长期固定规则不会改变。</span></div>
          <div className="week-fixed-editor__context">
            <strong>{editing.name}</strong>
            <small>{editing.date ? `${formatShortDate(editing.date)} ${weekdayLabel(editing.date)}` : '每周一次'}</small>
          </div>
          <label className="quantity-adjuster__field">
            <span>{editing.sourceType === 'dish' && editing.quantity === undefined ? '本周菜品批次' : '本周采购数量'}</span>
            <span className="quantity-adjuster__input">
              <input
                aria-label={`${editing.name}本周采购数量`}
                type="number"
                min="0.001"
                step={editing.sourceType === 'dish' && editing.quantity === undefined || requiresIntegerQuantity(editing.unit ?? '') ? '1' : 'any'}
                inputMode={editing.sourceType === 'dish' && editing.quantity === undefined || requiresIntegerQuantity(editing.unit ?? '') ? 'numeric' : 'decimal'}
                value={draftAmount}
                onChange={(event) => setDraftAmount(event.target.value)}
              />
              <strong>{editing.sourceType === 'dish' && editing.quantity === undefined ? '批' : editing.unit}</strong>
            </span>
          </label>
          {error && <p className="form-error" role="alert">{error}</p>}
          <div className="week-fixed-editor__actions">
            <button className="button button--ghost" type="button" onClick={() => setEditing(null)}>取消</button>
            <button className="button button--primary" type="submit">保存本周数量</button>
          </div>
          <button className="week-fixed-editor__remove" type="button" onClick={disableLine}>本周不采购这项</button>
          {(editing.adjusted || editing.disabled || overrides.some((item) => overrideKey(item) === overrideKey(editing))) && (
            <button className="week-fixed-editor__restore" type="button" onClick={restoreLine}><RefreshIcon size={18} />恢复长期规则的默认数量</button>
          )}
        </form>
      ) : (
        <div className="week-fixed-page" data-qa="weekly-fixed-purchase-adjustments">
          <div className="inline-alert week-fixed-page__notice">
            <CheckIcon size={20} />
            <span><strong>只影响本周</strong><small>长期规则和以后每周都不会改变。</small></span>
          </div>

          {!resolved ? (
            <div className="fixed-rules-empty"><strong>固定采购正在准备</strong><p>请关闭后重新加载本周计划再试。</p></div>
          ) : lines.length === 0 ? (
            <div className="fixed-rules-empty"><strong>本周没有固定采购</strong><p>可前往“固定采购设置”新增长期规则。</p></div>
          ) : (
            <>
              {dailyGroups.map(([date, group]) => (
                <section className="week-fixed-group" key={date}>
                  <header><strong>{formatShortDate(date)} {weekdayLabel(date)}</strong><span>{group.length} 项</span></header>
                  <div className="week-fixed-list">
                    {group.map((line) => (
                      <button className={line.disabled ? 'is-disabled' : line.adjusted ? 'is-adjusted' : ''} type="button" onClick={() => openLine(line)} key={overrideKey(line)} aria-label={`调整${line.name}本周固定采购`}>
                        <span><strong>{line.name}</strong><small>{line.restoringDefault ? '将恢复长期默认，保存后生效' : line.disabled ? '本周已取消' : line.adjusted ? '本周数量已调整' : '固定规则自动加入'}</small></span>
                        <strong>{lineAmount(line)}</strong><ChevronRightIcon size={20} />
                      </button>
                    ))}
                  </div>
                </section>
              ))}
              {weeklyLines.length > 0 && (
                <section className="week-fixed-group">
                  <header><strong>本周一次</strong><span>{weeklyLines.length} 项</span></header>
                  <div className="week-fixed-list">
                    {weeklyLines.map((line) => (
                      <button className={line.disabled ? 'is-disabled' : line.adjusted ? 'is-adjusted' : ''} type="button" onClick={() => openLine(line)} key={overrideKey(line)} aria-label={`调整${line.name}本周固定采购`}>
                        <span><strong>{line.name}</strong><small>{line.restoringDefault ? '将恢复长期默认，保存后生效' : line.disabled ? '本周已取消' : line.adjusted ? '本周数量已调整' : '固定规则自动加入'}</small></span>
                        <strong>{lineAmount(line)}</strong><ChevronRightIcon size={20} />
                      </button>
                    ))}
                  </div>
                </section>
              )}
            </>
          )}

          {error && <p className="form-error" role="alert">{error}</p>}
          <div className="week-fixed-page__actions">
            {overrides.length > 0 && <button className="button button--ghost" type="button" onClick={() => setDraftOverrides([])}><RefreshIcon size={19} />全部恢复默认</button>}
            <button className="button button--primary" type="button" disabled={!changed || saving} onClick={() => void submit()}>{saving ? '正在保存…' : changed ? '保存本周调整' : '本周没有新修改'}</button>
          </div>
        </div>
      )}
    </Modal>
  );
}
